<template>
  <div
    class="chip-wrapper"
    :class="[{ 'icon-right-padding': iconRight, clickable: clickable, 'is-loading': loading }, color]"
    :aria-busy="loading ? 'true' : null"
    v-on="$listeners"
  >
    <UserAvatar v-if="user" size="xs" :user="user" class="user-avatar" />
    <span v-if="loading" class="loading-slot right-margin"><n-loading size="s" dark /></span>
    <n-icon v-else-if="icon" class="right-margin" :icon="icon" size="xs" />
    <span :class="{ cutoff: cutoffText, closable }">{{ name }}</span>
    <div v-if="closable" class="close-button" @click="$emit('close')">
      <n-icon icon="x" size="s" />
    </div>
    <n-icon v-if="iconRight" class="left-margin" :icon="iconRight" size="s" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { ChipColor } from '@/modules/space/models/Space'
import User from '@/modules/user/models/User'
import UserAvatar from '@/modules/global/components/User/UserAvatar.vue'
import NLoading from './NLoading.vue'

@Component({
  components: { UserAvatar, NLoading }
})
export default class NChip extends Vue {
  @Prop() private name!: string
  @Prop() private color!: ChipColor
  @Prop() private icon!: string
  @Prop() private iconRight!: string
  @Prop() private user!: User
  @Prop({ default: false, type: Boolean }) private clickable!: boolean
  @Prop({ default: false, type: Boolean }) private closable!: boolean
  @Prop({ default: false, type: Boolean }) private cutoffText!: boolean
  @Prop({ default: false, type: Boolean }) private loading!: boolean
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.chip-wrapper {
  @include notch-text-label-light-M;
  border-radius: $notch-radius-full;
  height: $notch-space-6x;
  width: fit-content;
  padding: 0 $notch-space-3x;
  background: $notch-color-neutral-sand-200;
  color: $notch-color-neutral-grey-900;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.icon-right-padding {
  @extend .chip-wrapper;
  padding: $notch-space-1x $notch-space-2x $notch-space-1x $notch-space-3x;
}

.clickable {
  cursor: pointer;
}

.loading-slot {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: $notch-space-3x;
  height: $notch-space-3x;
}

.is-loading {
  pointer-events: none;
  opacity: 0.6;
}

.closable {
  margin-right: $notch-space-2x;
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.cutoff {
  max-width: $notch-space-30x;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.close-button {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: $notch-color-neutral-sand-600;
}

.right-margin {
  margin-right: $notch-space-2x;
}

.left-margin {
  margin-left: $notch-space-1x;
}

.success {
  background-color: $notch-color-semantic-success-150;
}

.grey {
  background-color: $notch-color-neutral-grey-200;
}

.info {
  background-color: $notch-color-semantic-info-150;
}

.accent {
  background-color: $notch-color-semantic-accent-150;
}

.sand {
  background-color: $notch-color-neutral-sand-100;
}

.alert {
  background-color: $notch-color-semantic-alert-150;
}

.user-avatar {
  margin-right: $notch-space-1x;
}
</style>
